import React, { useState, useEffect } from "react"; 
import { useNavigate } from "react-router-dom";
import api from "../../services/api";
import { useOportunidades } from "../../context/OportunidadesContext"; // Context adaptado para oportunidades
import Sidebar from "../../components/sidebar/Sidebar";

import {
  PageLayout,
  Container,
  Header,
  Title,
  Subtitle,
  SearchInput,
  AddButton,
  Table,
  TableHeader,
  TableRow,
  TableCell,
  StatusBadge,
  ActionIcons,
  EventInfo,
  EventTitle,
  EventSubtitle,
  SearchWrapper,
  SortWrapper,
  HighlightRow,
  ModalContent,
  ModalOverlay,
  ModalImageHeader,
  ModalSection,
  ModalInfoItem,
  ModalInfoIcon,
  ModalInfoText,
  ModalTag,
  ModalRememberButton,
  ModalCloseButton
} from "./styles";

import {
  Eye,
  Edit,
  Trash2,
  Plus,
  Search,
  Star,
  Calendar,
  MapPin,
  Users,
  X,
  CalendarCheck,
  Clock, 
} from "lucide-react";


/* 🔹 Helper imagem */
const getImagemUrl = (imagem) => {
  if (!imagem) return "https://placehold.co/600x200?text=Sem+Imagem";
  if (imagem.startsWith("http")) return imagem;

  const baseURL = api.defaults.baseURL;
  return `${baseURL}/${imagem.replace(/^\/?/, "")}`;
};

/* 🔹 Helper data (MongoDB) */
const formatarData = (data) => {
  if (!data) return "—";
  if (typeof data === "object" && data.$date) {
    return new Date(data.$date).toLocaleDateString("pt-BR");
  }
  return new Date(data).toLocaleDateString("pt-BR");
};

const Oportunidades = () => {
  const navigate = useNavigate();
  const { oportunidades = [], setOportunidades } = useOportunidades() || {};

  const [selectedId, setSelectedId] = useState(null);
  const [sortType, setSortType] = useState("az");
  const [query, setQuery] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [oportunidadeSelecionada, setOportunidadeSelecionada] = useState(null);
  const [loading, setLoading] = useState(true);

  // 🔹 Buscar oportunidades
  useEffect(() => {
    const fetchOportunidades = async () => {
      try {
        const response = await api.get("/api/content/oportunidades");
        setOportunidades(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error("Erro ao buscar oportunidades:", error);
        setOportunidades([]);
      } finally {
        setLoading(false);
      }
    };
    fetchOportunidades();
  }, [setOportunidades]);

  // 🔹 Deletar oportunidade
  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Deseja excluir esta oportunidade?")) return;

    try {
      await api.delete(`/api/content/oportunidades/${id}`);
      setOportunidades((prev) => prev.filter((o) => (o._id || o.id) !== id));
    } catch (error) {
      alert("Erro ao deletar oportunidade.");
    }
  };

  // 🔹 Toggle destaque
  const toggleHighlight = async (id, e) => {
    e.stopPropagation();
    const oportunidadeAtual = oportunidades.find((o) => (o._id || o.id) === id);
    if (!oportunidadeAtual) return;

    const novoValor = !oportunidadeAtual.destacado;
    setOportunidades((prev) =>
      prev.map((o) =>
        (o._id || o.id) === id ? { ...o, destacado: novoValor } : o
      )
    );

    try {
      await api.put(`/api/content/oportunidades/${id}/highlight`);
    } catch (error) {
      setOportunidades((prev) =>
        prev.map((o) =>
          (o._id || o.id) === id ? { ...o, destacado: oportunidadeAtual.destacado } : o
        )
      );
    }
  };

  // 🔹 Filtro
  const filtered = oportunidades.filter(
    (o) =>
      o.titulo?.toLowerCase().includes(query.toLowerCase()) ||
      o.empresa?.toLowerCase().includes(query.toLowerCase()) ||
      o.cargo?.toLowerCase().includes(query.toLowerCase())
  );

  // 🔹 Ordenação
  const sortOportunidades = (list) => {
    const copy = [...list];
    if (sortType === "az") copy.sort((a, b) => a.titulo.localeCompare(b.titulo));
    if (sortType === "za") copy.sort((a, b) => b.titulo.localeCompare(a.titulo));
    if (sortType === "highlighted")
      copy.sort(
        (a, b) =>
          (b.destacado ? 1 : 0) - (a.destacado ? 1 : 0) ||
          a.titulo.localeCompare(b.titulo)
      );
    return copy;
  };

  const displayed = sortOportunidades(filtered);

  return (
    <PageLayout>
      <Sidebar />

      <Container>
        <Header>
          <div>
            <Title>Gerenciar oportunidades</Title>
            <Subtitle>Crie e gerencie oportunidades da comunidade</Subtitle>
          </div>

          <AddButton onClick={() => navigate("/criar-oportunidade")}>
            <Plus size={18} />
            Nova oportunidade
          </AddButton>
        </Header>

        <SortWrapper>
          <label>Ordenar por:</label>
          <select value={sortType} onChange={(e) => setSortType(e.target.value)}>
            <option value="az">A–Z</option>
            <option value="za">Z–A</option>
            <option value="highlighted">Destaques</option>
          </select>
        </SortWrapper>

        <SearchWrapper>
          <Search size={18} />
          <SearchInput
            placeholder="Pesquisar oportunidades..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </SearchWrapper>

        <Table>
          <thead>
            <TableRow header>
              <TableHeader>Título</TableHeader>
              <TableHeader>Empresa</TableHeader>
              <TableHeader>Cargo</TableHeader>
              <TableHeader>Status</TableHeader>
              <TableHeader>Ações</TableHeader>
            </TableRow>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan="5" style={{ padding: 40, textAlign: "center" }}>
                  Carregando oportunidades...
                </td>
              </tr>
            ) : displayed.length === 0 ? (
              <tr>
                <td colSpan="5" style={{ padding: 40, textAlign: "center" }}>
                  Nenhuma oportunidade encontrada.
                </td>
              </tr>
            ) : (
              displayed.map((oportunidade) => {
                const id = oportunidade._id || oportunidade.id;
                return (
                  <HighlightRow
                    key={id}
                    highlighted={oportunidade.destacado}
                    selected={selectedId === id}
                    onClick={() => setSelectedId(id)}
                  >
                    <TableCell>
                      <EventInfo>
                        {oportunidade.destacado && <span>⭐ DESTACADO</span>}
                        <EventTitle>{oportunidade.titulo}</EventTitle>
                        <EventSubtitle>{oportunidade.descricao}</EventSubtitle>
                      </EventInfo>
                    </TableCell>

                    <TableCell>{oportunidade.empresa}</TableCell>
                    <TableCell>{oportunidade.cargo}</TableCell>
                    <TableCell>
                      <StatusBadge>{oportunidade.status}</StatusBadge>
                    </TableCell>

                    <TableCell>
                      <ActionIcons>
                        <Star
                          size={18}
                          color={oportunidade.destacado ? "#2563eb" : "#9ca3af"}
                          onClick={(e) => toggleHighlight(id, e)}
                        />
                        <Eye
                          size={18}
                          onClick={(e) => {
                            e.stopPropagation();
                            setOportunidadeSelecionada(oportunidade);
                            setShowModal(true);
                          }}
                        />
                        <Edit
                          size={18}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/editar-oportunidades/${id}`);
                          }}
                        />
                        <Trash2
                          size={18}
                          color="#EF4444"
                          onClick={(e) => handleDelete(id, e)}
                        />
                      </ActionIcons>
                    </TableCell>
                  </HighlightRow>
                );
              })
            )}
          </tbody>
        </Table>
      </Container>

      {/* 🔹 MODAL VISUALIZAÇÃO */}
{showModal && oportunidadeSelecionada && (
  <ModalOverlay onClick={() => setShowModal(false)}>
    <ModalContent onClick={(e) => e.stopPropagation()}>
      <ModalCloseButton onClick={() => setShowModal(false)}>
        <X size={18} />
      </ModalCloseButton>

      {/* IMAGEM */}
      <ModalImageHeader>
        {oportunidadeSelecionada.imagem ? (
          <img
            src={getImagemUrl(oportunidadeSelecionada.imagem)}
            alt={oportunidadeSelecionada.titulo}
          />
        ) : (
          <div className="placeholder">Sem imagem</div>
        )}
      </ModalImageHeader>

      <div style={{ padding: "1.5rem", overflowY: "auto" }}>
        <h2>{oportunidadeSelecionada.titulo}</h2>
        {oportunidadeSelecionada.descricao && <p>{oportunidadeSelecionada.descricao}</p>}

        {/* INFORMAÇÕES PRINCIPAIS */}
        <ModalSection>
          <ModalInfoItem>
            <ModalInfoIcon color="#2563eb">
              <Calendar size={18} />
            </ModalInfoIcon>
            <ModalInfoText>
              Prazo início: {formatarData(oportunidadeSelecionada.dataInicio)}
            </ModalInfoText>
          </ModalInfoItem>

          <ModalInfoItem>
            <ModalInfoIcon color="#16a34a">
              <CalendarCheck size={18} />
            </ModalInfoIcon>
            <ModalInfoText>
              Prazo fim: {formatarData(oportunidadeSelecionada.dataFim)}
            </ModalInfoText>
          </ModalInfoItem>

          <ModalInfoItem>
            <ModalInfoIcon color="#0ea5e9">
              <Users size={18} />
            </ModalInfoIcon>
            <ModalInfoText>
              {oportunidadeSelecionada.vagas
                ? `${oportunidadeSelecionada.vagas} vagas`
                : "Vagas ilimitadas"}
            </ModalInfoText>
          </ModalInfoItem>

          <ModalInfoItem>
            <ModalInfoIcon color="#f59e0b">
              <Clock size={18} />
            </ModalInfoIcon>
            <ModalInfoText>
              Horário de trabalho: {oportunidadeSelecionada.horaTrabalho || "Não informado"}
            </ModalInfoText>
          </ModalInfoItem>

          <ModalInfoItem>
            <ModalInfoIcon color="#16a34a">
              <MapPin size={18} />
            </ModalInfoIcon>
            <ModalInfoText>{oportunidadeSelecionada.local || "Não informado"}</ModalInfoText>
          </ModalInfoItem>

          <ModalInfoItem>
            <ModalInfoIcon color="#2563eb">
              <Users size={18} />
            </ModalInfoIcon>
            <ModalInfoText>Empresa: {oportunidadeSelecionada.empresa || "Não informada"}</ModalInfoText>
          </ModalInfoItem>

          <ModalInfoItem>
            <ModalInfoIcon color="#2563eb">
              <Users size={18} />
            </ModalInfoIcon>
            <ModalInfoText>Cargo: {oportunidadeSelecionada.cargo || "Não informado"}</ModalInfoText>
          </ModalInfoItem>

          {oportunidadeSelecionada.salario && (
            <ModalInfoItem>
              <ModalInfoIcon color="#16a34a">
                <Users size={18} />
              </ModalInfoIcon>
              <ModalInfoText>Salário: {oportunidadeSelecionada.salario}</ModalInfoText>
            </ModalInfoItem>
          )}

          <ModalInfoItem>
            <ModalInfoIcon color={oportunidadeSelecionada.destacado ? "#fbbf24" : "#9ca3af"}>
              <Star size={18} />
            </ModalInfoIcon>
            <ModalInfoText>
              {oportunidadeSelecionada.destacado ? "Oportunidade Destacada" : "Não Destacada"}
            </ModalInfoText>
          </ModalInfoItem>
        </ModalSection>

        {/* SOBRE A EMPRESA */}
        {oportunidadeSelecionada.sobreEmpresa && (
          <ModalSection>
            <strong>Sobre a Empresa</strong>
            <p>{oportunidadeSelecionada.sobreEmpresa}</p>
          </ModalSection>
        )}

        {/* CONTEÚDOS */}
        <ModalSection>
          <strong>Conteúdos</strong>
          {oportunidadeSelecionada.conteudos?.length > 0 ? (
            oportunidadeSelecionada.conteudos.map((conteudo, idx) => (
              <div key={idx} style={{ marginTop: 8 }}>
                <p><strong>{conteudo.titulo}</strong></p>
                <ul>
                  {conteudo.itens
                    .filter((item) => item.trim() !== "")
                    .map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                </ul>
              </div>
            ))
          ) : (
            <p>Nenhum conteúdo cadastrado.</p>
          )}
        </ModalSection>

        {/* CONTATOS */}
        <ModalSection>
          <strong>Contatos</strong>
          {oportunidadeSelecionada.contatos?.some(c => c.telefone || c.email || c.redeSocial) ? (
            oportunidadeSelecionada.contatos.map((c, i) => (
              <div key={i}>
                {c.telefone && <p>📞 {c.telefone}</p>}
                {c.email && <p>📧 {c.email}</p>}
                {c.redeSocial && <p>🌐 {c.redeSocial}</p>}
              </div>
            ))
          ) : (
            <p>Nenhum contato informado.</p>
          )}
        </ModalSection>

        {/* STATUS */}
        <ModalSection>
          <strong>Status</strong>
          <StatusBadge>{oportunidadeSelecionada.status}</StatusBadge>
        </ModalSection>
      </div>
    </ModalContent>
  </ModalOverlay>
)}

    </PageLayout>
  );
};

export default Oportunidades;
